import type { ValidationFinding } from "@/api/hooks";

/**
 * Failure classification for validation findings.
 *
 * Issue #7 requires distinguishing schema, compatibility, base-identity, and
 * locked-floor failures. The contract does not support that directly:
 * `ValidationFinding.code` is a bare `type: string` described only as
 * "machine-readable finding code from the base-owned validator", and the code
 * vocabulary is published nowhere in `editor-service.openapi.yaml`.
 *
 * So classification is a frontend inference, and the only safe way to make one
 * is to make it visibly incomplete. A prefix convention was chosen over an
 * exact code-to-class table because the backend can add codes freely without
 * this file silently misclassifying them: `schema.missing_required_field`
 * classifies correctly on the day it ships, where a table would need an edit
 * first. Both fail identically on an unknown prefix; the prefix rule just has
 * no per-code maintenance debt for #33–#36 to inherit.
 *
 * **Provisional until #16 confirms it.** Matching is exact and case-sensitive:
 * a code whose prefix is not listed here is `unclassified`, never guessed into
 * a class and never hidden. Its presence is itself a fail-closed condition for
 * candidate creation — see `useCandidateCreationGate`.
 */
export type FailureClass =
  | "schema"
  | "compatibility"
  | "base_identity"
  | "locked_floor"
  | "adopted_content"
  | "safety_evidence"
  | "clinical_dependency"
  | "unclassified";

/**
 * Ordered longest-prefix-first so a future `base.identity.` cannot be shadowed
 * by a shorter neighbour.
 */
const CLASS_BY_PREFIX: ReadonlyArray<readonly [string, FailureClass]> = [
  ["schema.", "schema"],
  // #1084: the typed clinical-dependency report — a reachable Pathway lacking
  // the content its consumers read, or a flow declaration no conversation can
  // serve. LONGER than `compat.`, and listed before it, because it IS a
  // `compat.` code by design: a bundle that predates this class still
  // classifies it as compatibility and keeps the candidate gate closed, while
  // this bundle can name the class and render the finding's `location`.
  ["compat.clinical_dependency", "clinical_dependency"],
  ["compat.", "compatibility"],
  ["base.", "base_identity"],
  ["floor.", "locked_floor"],
  // #29 G2b: the Editor Service's own refusals for a project's adopted
  // library content, which reach this classifier through `POST /validations`
  // exactly like the base-owned families above. Without the prefix they land
  // in `unclassified`, which fails the candidate-creation gate and replaces a
  // typed, actionable sentence with "report the codes to the service owner".
  ["adoption.", "adopted_content"],
  // #853: the Editor Service's own refusal for the EVIDENCE behind a governed
  // safety choice — today `safety.final_questioning_evidence_stale`, raised
  // when a Project's final safety questioning is off and the confirmation
  // recorded for it no longer covers the current draft revision or the
  // consequence wording this deployment states.
  //
  // Its own class, and not `locked_floor`. The four classes above all describe
  // what the ENGINE said about a tree, and this says something the engine
  // cannot see at all: the tree it judges is identical either way (the freeze
  // pipeline's own note on the prefix). Without an entry here it lands in
  // `unclassified`, which fails the candidate-creation gate for the wrong
  // reason and replaces an actionable sentence — re-read the consequences and
  // confirm again — with "report the codes to the service owner".
  ["safety.", "safety_evidence"],
];

export function classifyFindingCode(code: string): FailureClass {
  for (const [prefix, failureClass] of CLASS_BY_PREFIX) {
    if (code.startsWith(prefix)) {
      return failureClass;
    }
  }
  return "unclassified";
}

export function classifyFinding(finding: ValidationFinding): FailureClass {
  return classifyFindingCode(finding.code);
}

/**
 * True when any finding could not be classified.
 *
 * This is deliberately not "any finding is an error". A report the browser
 * cannot fully interpret is a report it cannot certify as clean, whatever the
 * severities say — which is why this feeds the candidate-creation gate rather
 * than only the display.
 */
export function hasUnclassifiedFindings(
  findings: readonly ValidationFinding[],
): boolean {
  return findings.some((finding) => classifyFinding(finding) === "unclassified");
}

/**
 * The guard floor is the one class that also has a lock-state counterpart in
 * the effective projection (`FieldState.lock.reason === "safety_floor"`).
 * Naming the test keeps the join legible where it is performed.
 */
export function isGuardFloorClass(failureClass: FailureClass): boolean {
  return failureClass === "locked_floor";
}

/** Severity ordering for display: most serious first, stable within a rank. */
const SEVERITY_RANK = { error: 0, warning: 1, info: 2 } as const;

export function bySeverity(
  a: ValidationFinding,
  b: ValidationFinding,
): number {
  const ranks = SEVERITY_RANK as Readonly<Record<string, number>>;
  return (ranks[a.severity] ?? 3) - (ranks[b.severity] ?? 3);
}

export type SeverityCounts = Record<
  ValidationFinding["severity"],
  number
> & { other: number };

export function countBySeverity(
  findings: readonly ValidationFinding[],
): SeverityCounts {
  const counts: SeverityCounts = { error: 0, warning: 0, info: 0, other: 0 };
  for (const finding of findings) {
    const severity = finding.severity as string;
    if (severity === "error" || severity === "warning" || severity === "info") {
      counts[severity] += 1;
    } else {
      counts.other += 1;
    }
  }
  return counts;
}
